// File: frontend/src/pages/partner/partnerShared.js
//
// One vocabulary for the Ambassador surfaces: how money is written, how a tier
// looks, what each client state is called.
//
// ── WHY THIS IS SHARED RATHER THAN PER-PAGE ─────────────────────────────────
//
// Overview, Clients, Earnings and Program all show the same tier and the same
// money. Four copies of "what colour is Gold" is four chances for them to
// disagree, and an Ambassador reading two different colours for one tier on two
// screens learns not to trust either.
//
// ── COLOUR DISCIPLINE ───────────────────────────────────────────────────────
//
// Every colour here resolves from the THEME, never a hex literal. A raw hex is
// the one way a PesaFii screen breaks in dark mode — the theme is what flips —
// and financialsUiDiscipline.test.js exists because that had already happened
// twice. Tier accents are the one place a palette is unavoidable, so they are
// expressed as palette KEYS and resolved through the theme at render time.

import { alpha } from "@mui/material";

/** UGX is a whole-shilling currency everywhere in this product. */
export const ugx = (n) => `UGX ${Math.round(Number(n) || 0).toLocaleString()}`;

/** Compact, for a headline figure that must not wrap on a phone. */
export const ugxShort = (n) => {
  const v = Math.round(Number(n) || 0);
  if (Math.abs(v) >= 1_000_000) return `UGX ${(v / 1_000_000).toFixed(v % 1_000_000 === 0 ? 0 : 1)}M`;
  if (Math.abs(v) >= 10_000)    return `UGX ${Math.round(v / 1000)}k`;
  return ugx(v);
};

export const fmtDate = (d) => {
  if (!d) return "—";
  const x = new Date(d);
  return Number.isNaN(x.getTime())
    ? "—"
    : x.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

export const fmtDateTime = (d) => {
  if (!d) return "—";
  const x = new Date(d);
  return Number.isNaN(x.getTime())
    ? "—"
    : x.toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

// ── TIERS ───────────────────────────────────────────────────────────────────
//
// `paletteKey` is resolved through the theme so both modes stay legible. Bronze
// deliberately reads as ordinary text rather than a status colour: it is where
// everyone starts, and colouring it would make "not yet anything" look like a
// state to worry about.
export const TIERS = {
  bronze:   { label: "Bronze",   paletteKey: null,      threshold: 0 },
  silver:   { label: "Silver",   paletteKey: "info",    threshold: 10 },
  gold:     { label: "Gold",     paletteKey: "warning", threshold: 50 },
  platinum: { label: "Platinum", paletteKey: "primary", threshold: 100 },
  diamond:  { label: "Diamond",  paletteKey: "success", threshold: 250 },
};

export const TIER_ORDER = ["bronze", "silver", "gold", "platinum", "diamond"];

export function tierLabel(key) {
  return TIERS[key]?.label || "Bronze";
}

export function tierColor(theme, key) {
  const p = TIERS[key]?.paletteKey;
  return p ? theme.palette[p].main : theme.palette.text.secondary;
}

export function tierSoftBg(theme, key, weight = 0.12) {
  return alpha(tierColor(theme, key), weight);
}

// ── CLIENT STATES ───────────────────────────────────────────────────────────
//
// The four states the backend derives, with the words an Ambassador sees. `tone`
// feeds StatusBadge, which pairs every tone with a distinct label — colour is
// never the only carrier of meaning (§13.4).
export const CLIENT_STATUS = {
  ACTIVE:     { label: "Active",     tone: "active",  hint: "Generating PesaFii revenue" },
  SETTING_UP: { label: "Setting up", tone: "pending", hint: "Signed up, not trading yet" },
  REGISTERED: { label: "Registered", tone: "info",    hint: "Account created, not verified" },
  INACTIVE:   { label: "Inactive",   tone: "expired", hint: "No qualifying revenue in 30 days" },
};

export const clientStatusLabel = (s) => CLIENT_STATUS[s]?.label || "—";
export const clientStatusTone  = (s) => CLIENT_STATUS[s]?.tone  || "info";
export const clientStatusHint  = (s) => CLIENT_STATUS[s]?.hint  || "";

// ── LEDGER ENTRIES ──────────────────────────────────────────────────────────
//
// Plain words. An Ambassador should not have to learn the schema to read their own
// statement.
export const ENTRY_LABEL = {
  commission:          "Commission",
  milestone_reward:    "Milestone reward",
  commission_reversal: "Commission adjustment",
  manual_adjustment:   "Adjustment",
  withdrawal:          "Withdrawal",
  withdrawal_reversal: "Withdrawal returned",
};

export const entryLabel = (t) => ENTRY_LABEL[t] || "Entry";

/**
 * Money colour, following the rule the Financials pages already set:
 *
 *   GREEN MEANS ONE THING — money that came IN.
 *
 * Everything else is ordinary text. A red for every outgoing line would make a
 * normal withdrawal look like a problem.
 */
export function amountColor(theme, amount) {
  return Number(amount) > 0 ? theme.palette.success.main : theme.palette.text.primary;
}

export const signed = (n) => {
  const v = Math.round(Number(n) || 0);
  return `${v > 0 ? "+" : v < 0 ? "−" : ""} ${ugx(Math.abs(v))}`;
};

/**
 * The referral URL an Ambassador shares.
 *
 * Built from the browser's own origin rather than a hardcoded domain, so a
 * preview deployment, a staging host and production each produce a link that
 * actually works. A literal "pesafii.net" here would hand a staging tester a
 * link into production.
 */
export function referralUrl(code) {
  const origin = typeof window !== "undefined" && window.location
    ? window.location.origin
    : "";
  return `${origin}/r/${encodeURIComponent(String(code || ""))}`;
}

/** Progress toward the next tier, as a 0–100 number for a bar. */
export function tierProgress({ activeClients, currentTier, nextTier }) {
  if (!nextTier) return 100;
  const floor = TIERS[currentTier]?.threshold ?? 0;
  const ceil = nextTier.threshold;
  if (ceil <= floor) return 100;
  const pct = ((Number(activeClients) || 0) - floor) / (ceil - floor);
  return Math.max(0, Math.min(100, Math.round(pct * 100)));
}

/**
 * Map a backend error code to something worth reading.
 *
 * A raw "Network error" for a known, explainable refusal is the failure the
 * design system's error taxonomy exists to prevent.
 */
export const PARTNER_ERRORS = {
  NOT_A_PARTNER:        "You have not joined PesaFii Ambassadors yet.",
  PARTNER_SUSPENDED:    "Your Partner account is under review. Your referrals and earnings are safe.",
  PARTNER_DISABLED:     "Your Partner account has been closed.",
  PARTNER_PENDING:      "Your Partner account is awaiting activation.",
  PROGRAM_DISABLED:     "PesaFii Ambassadors is not open yet.",
  EMAIL_NOT_VERIFIED:   "Verify your email address before joining PesaFii Ambassadors.",
  ACCOUNT_INACTIVE:     "This account is not active.",
  PIN_NOT_SET:          "Set your Withdrawal PIN before making a transfer.",
  PIN_LOCKED:           "Too many incorrect attempts. Try again later or reset your PIN.",
  INSUFFICIENT_BALANCE: "That is more than your Ambassador Wallet holds.",
  BELOW_MINIMUM:        "That is below the minimum transfer amount.",
  QUOTE_MISMATCH:       "The transfer details changed. Please start again.",
  PARTNER_STATUS_UNAVAILABLE: "We could not check your Partner account just now. Please try again shortly.",
};

export function partnerError(err) {
  const code = err?.response?.data?.code || err?.code;
  if (code && PARTNER_ERRORS[code]) return PARTNER_ERRORS[code];
  return err?.response?.data?.error || err?.message || "Something went wrong.";
}
